import { useCallback, useEffect, useMemo, useState } from 'react'

import { Group, InputWrapper, Stack, Text, TextInput } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

function parseTime(
  timeStr: string,
  defaultValue: string | null = null,
): string | null {
  const formats = [
    'ha', // 4pm
    'h:mma', // 4:30pm
    'hh:mma', // 04:30pm
    'HH:mm', // 12:01
    'H:mm', // 9:30
    'hha', // 04pm
  ]
  const normTimeStr = timeStr.trim().toLowerCase()
  for (const format of formats) {
    const parsed = dayjs(normTimeStr, format, true)
    if (parsed.isValid()) {
      return parsed.format('HH:mm:ss')
    }
  }

  return defaultValue
}

interface TowerDateTimeInputProps {
  value?: string | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  allowFuture?: boolean
  label?: string
  required?: boolean
  description?: React.ReactNode
  disabled?: boolean
  onChange?: (value: string | null) => void
  'data-testid'?: string
}

function getTimezoneOffsetString() {
  const offset = -new Date().getTimezoneOffset()
  const hours = Math.floor(Math.abs(offset) / 60)
  const minutes = Math.abs(offset) % 60
  const sign = offset >= 0 ? '+' : '-'
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function TowerDateTimeInput({
  value: initialValue,
  size,
  allowFuture = false,
  label = 'Timestamp',
  required,
  description,
  disabled,
  onChange,
  'data-testid': dataTestId,
}: TowerDateTimeInputProps) {
  const [value, setValue] = useState(initialValue || null)
  const [timeDisplayValue, setTimeDisplayValue] = useState<string>('')

  const { date, time } = useMemo(() => {
    if (!value) return { date: null, time: null }
    const parsed = dayjs(value)
    return {
      date: parsed.format('YYYY-MM-DD'),
      time: parsed.format('HH:mm:ss'),
    }
  }, [value])

  useEffect(() => {
    setTimeDisplayValue(time || '')
  }, [time])

  const handleChange = useCallback(
    (newValue: string | null) => {
      setValue(newValue)
      onChange?.(newValue)
    },
    [onChange],
  )

  const handleDateChange = useCallback(
    (newDate: string | null) => {
      if (!newDate) {
        handleChange(null)
      } else {
        const newTime = time || '12:00:00'
        const combined = `${newDate} ${newTime}`
        const isoString = dayjs(combined).toISOString()
        handleChange(isoString)
      }
    },
    [handleChange, time],
  )

  const handleTimeBlur = useCallback(() => {
    if (!timeDisplayValue) return
    const parsedTime = parseTime(timeDisplayValue, null)
    if (parsedTime) {
      const newDate = date || dayjs().format('YYYY-MM-DD')
      const combined = `${newDate} ${parsedTime}`
      const isoString = dayjs(combined).toISOString()
      handleChange(isoString)
    } else {
      setTimeDisplayValue(time || '')
    }
  }, [timeDisplayValue, date, handleChange, time])

  return (
    <Stack>
      <InputWrapper label={label} required={required} description={description}>
        <Group>
          <DateInput
            valueFormat="YYYY-MM-DD"
            placeholder="Date"
            value={date}
            onChange={handleDateChange}
            maxDate={allowFuture ? undefined : dayjs().toDate()}
            size={size}
            disabled={disabled}
            data-testid={dataTestId}
          />
          @
          <TextInput
            placeholder="HH:mm:ss"
            value={timeDisplayValue}
            onChange={(e) => {
              setTimeDisplayValue(e.currentTarget.value)
            }}
            onBlur={handleTimeBlur}
            onFocus={(e) => {
              if (e.target.selectionStart === e.target.selectionEnd) {
                e.target.select()
              }
            }}
            size={size}
            disabled={disabled}
          />
          <Text size="sm" c="dimmed">
            {Intl.DateTimeFormat().resolvedOptions().timeZone}
            &nbsp;
            {getTimezoneOffsetString()}
          </Text>
        </Group>
      </InputWrapper>
    </Stack>
  )
}
